import React, { useContext, useEffect } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { InstallContext, Steps } from "./InstallContext";
import classNames from "classnames";
import { Link, Paper, Title, useToggleState } from "@yoast/ui-library";
import * as styles from "./InstallStyles.module.scss";
import { IncludedServices } from "../Subscription/IncludedServices";
import { SubscriptionDetailContext, SubscriptionDetailProvider } from "../../context/SubscriptionDetailContext";
import { ConnectedEmailAccess } from "../Subscription/ConnectedEmailAccess";
import { StepNavigation } from "./StepNavigation";

const messages = defineMessages( {
	title: {
		id: "linkGoogleAccounts.title",
		defaultMessage: "Let’s link your {service} to your subscription!",
		values: { service: "Google account" },
	},
	titleWithPlugin: {
		id: "linkGoogleAccounts.title",
		defaultMessage: "Before installing {plugin}…",
		values: { plugin: "Yoast SEO Premium" },
	},
	subtitleWithPlugin: {
		id: "linkGoogleAccounts.subtitle",
		defaultMessage: "Your {plugin} purchase includes free access to our {service}.",
		values: { plugin: "Yoast SEO Premium", service: "Google Docs add-on" },
	},
	note: {
		id: "linkGoogleAccounts.note",
		defaultMessage: "Note: ",
	},
	automaticallyLinkedNote: {
		id: "linkGoogleAccounts.automaticallyLinkedNote",
		defaultMessage: "We’ve automatically linked your Google account below, assuming your email is associated with your Google account. " +
			"You can unlink it if you'd prefer to use a different one.",
	},
	stepOneTitle: {
		id: "linkGoogleAccounts.stepOne",
		defaultMessage: "Step 1: Link your Google account",
	},
	stepOneDescription: {
		id: "linkGoogleAccounts.stepOneDescription",
		defaultMessage: "Make sure the right Google account is linked to get access to the Google Docs add-on. " +
			"If you have multiple seats, you can invite others by linking their Google accounts.",
	},
	stepTwoTitle: {
		id: "linkGoogleAccounts.stepTwoTitle",
		defaultMessage: "Step 2: Install the add-on",
	},
	stepTwoDescription: {
		id: "linkGoogleAccounts.stepTwoDescription",
		defaultMessage: "Once your Google account is linked, you can install the add-on directly in your Google Workspace. " +
			"Need help? {installGuideLink}.",
		values: {
			installGuideLink: <Link target="_blank" href="https://yoa.st/yoast-seo-google-docs-installation-guide-checkout">
				Read our installation guide
			</Link>,
		},
	},
} );


/**
 * Renders the notice that informs the user that their email address is already linked to a Google account.
 * @returns {Element} The rendered notice.
 */
function LinkedSelfNotice() {
	const { subscription, request }            = useContext( SubscriptionDetailContext );
	const [ shouldShowNotice, , , showNotice ] = useToggleState( false );

	useEffect( () => {
		if ( subscription?.emailInvites?.usedSeats?.some( seat => seat.emailAddress === subscription.subscriberEmail ) ) {
			showNotice();
		}
	}, [ request.isFirstLoad ] );

	return ( <>
		{ shouldShowNotice && <p className={ styles.notice }>
			<FormattedMessage tagName={ "strong" } { ...messages.note } />
			<FormattedMessage { ...messages.automaticallyLinkedNote } />
		</p> }
	</> );
}

/**
 * Renders the downloading component. Which shows instructions about installing Yoast plugins.
 *
 * @returns {React.JSX.Element} The rendered component.
 */
function LinkGoogleAccounts() {
	const { subscriptionIdWithInvitableService: subscriptionId, steps } = useContext( InstallContext );
	const willInstallPremium                                            = steps.available.includes( Steps.DOWNLOAD );

	return (
		<SubscriptionDetailProvider subscriptionId={ subscriptionId }>
			<div className={ classNames( styles.ySpacing, styles.linkGoogleAccountStep ) }>
				{ willInstallPremium
					? <Title className={ styles.textCenter }>
						<FormattedMessage { ...messages.titleWithPlugin } />
					</Title>
					: <Title className={ styles.textCenter }>
						<FormattedMessage { ...messages.title } />
					</Title>
				}
				<hr />
				{ willInstallPremium &&
					<Title as="p" size="4"><FormattedMessage{ ...messages.subtitleWithPlugin } /></Title> }
				<LinkedSelfNotice />

				<div>
					<Title as="p" size="5"><FormattedMessage { ...messages.stepOneTitle } /></Title>
					<p><FormattedMessage { ...messages.stepOneDescription } /></p>
				</div>
				<Paper className={ styles.paper }>
					<ConnectedEmailAccess />
				</Paper>

				<div>
					<Title as="p" size="5"><FormattedMessage { ...messages.stepTwoTitle } /></Title>
					<p><FormattedMessage { ...messages.stepTwoDescription } /></p>
				</div>
				<Paper className={ styles.paper }>
					<IncludedServices />
				</Paper>

				<hr />
				<StepNavigation />
			</div>
		</SubscriptionDetailProvider>
	);
}


export default LinkGoogleAccounts;
